import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';

interface DialogOptions {
    title?: string;
    message: ReactNode;
    confirmText?: string;
    cancelText?: string;
    width?: string;
}

interface PromptOptions extends DialogOptions {
    defaultValue?: string;
    placeholder?: string;
}

interface DialogContextType {
    alert: (options: DialogOptions | string) => Promise<void>;
    confirm: (options: DialogOptions | string) => Promise<boolean>;
    prompt: (options: PromptOptions | string) => Promise<string | null>;
}

const DialogContext = createContext<DialogContextType | undefined>(undefined);

export const useDialog = () => {
    const context = useContext(DialogContext);
    if (!context) {
        throw new Error('useDialog must be used within a DialogProvider');
    }
    return context;
};

type DialogState = {
    isOpen: boolean;
    type: 'alert' | 'confirm' | 'prompt';
    options: PromptOptions;
    resolve: (value: any) => void;
    inputValue: string;
};

export const DialogProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [dialog, setDialog] = useState<DialogState | null>(null);
    const [isLight, setIsLight] = useState(false);

    useEffect(() => {
        const checkTheme = () => {
            try {
                const clientTheme = localStorage.getItem('crm-theme');
                const prefersLight = window.matchMedia('(prefers-color-scheme: light)').matches;
                setIsLight(clientTheme ? clientTheme === 'light' : prefersLight);
            } catch {
                setIsLight(false);
            }
        };
        
        checkTheme();
        window.addEventListener('storage', checkTheme);
        const mediaQuery = window.matchMedia('(prefers-color-scheme: light)');
        mediaQuery.addEventListener('change', checkTheme);
        
        return () => {
            window.removeEventListener('storage', checkTheme);
            mediaQuery.removeEventListener('change', checkTheme);
        };
    }, []);

    const openDialog = useCallback((type: 'alert' | 'confirm' | 'prompt', options: PromptOptions | string) => {
        return new Promise<any>((resolve) => {
            const parsedOptions: PromptOptions = typeof options === 'string' ? { message: options } : options;
            setDialog({
                isOpen: true,
                type,
                options: parsedOptions,
                resolve,
                inputValue: parsedOptions.defaultValue || ''
            });
        });
    }, []);

    const alert = useCallback((options: DialogOptions | string) => openDialog('alert', options), [openDialog]);
    const confirm = useCallback((options: DialogOptions | string) => openDialog('confirm', options), [openDialog]);
    const prompt = useCallback((options: PromptOptions | string) => openDialog('prompt', options), [openDialog]);

    const handleClose = (value: any) => {
        if (dialog) {
            dialog.resolve(value);
            setDialog(null);
        }
    };

    return (
        <DialogContext.Provider value={{ alert, confirm, prompt }}>
            {children}
            {dialog?.isOpen && (
                <div style={{
                    position: 'fixed',
                    top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    backdropFilter: 'blur(2px)',
                    zIndex: 999999,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    animation: 'fadeIn 0.2s ease'
                }}>
                    <style>
                        {`
                        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                        @keyframes slideUp { from { transform: translateY(10px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
                        `}
                    </style>
                    <div style={{
                        background: isLight ? '#ffffff' : '#18181b',
                        color: isLight ? '#1f2937' : '#f4f4f5',
                        border: `1px solid ${isLight ? '#e5e7eb' : '#3f3f46'}`,
                        borderRadius: '12px',
                        padding: '1.5rem',
                        width: '90%',
                        maxWidth: dialog.options.width || '400px',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 10px 10px -5px rgba(0, 0, 0, 0.1)',
                        animation: 'slideUp 0.2s ease',
                    }}>
                        <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.125rem', fontWeight: 600 }}>
                            {dialog.options.title || (dialog.type === 'alert' ? 'Alert' : dialog.type === 'confirm' ? 'Confirm' : 'Prompt')}
                        </h3>
                        
                        <div style={{ margin: '0 0 1.5rem 0', fontSize: '0.95rem', color: isLight ? '#4b5563' : '#a1a1aa', wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                            {dialog.options.message}
                        </div>

                        {dialog.type === 'prompt' && (
                            <input 
                                type="text"
                                autoFocus
                                value={dialog.inputValue}
                                onChange={e => setDialog({ ...dialog, inputValue: e.target.value })}
                                onKeyDown={e => { if (e.key === 'Enter') handleClose(dialog.inputValue); if (e.key === 'Escape') handleClose(null); }}
                                placeholder={dialog.options.placeholder || 'Enter value...'}
                                style={{
                                    width: '100%',
                                    padding: '0.75rem',
                                    marginBottom: '1.5rem',
                                    background: isLight ? '#f9fafb' : '#27272a',
                                    border: `1px solid ${isLight ? '#d1d5db' : '#3f3f46'}`,
                                    color: isLight ? '#111827' : '#f4f4f5',
                                    borderRadius: '6px',
                                    outline: 'none',
                                    boxSizing: 'border-box'
                                }}
                            />
                        )}

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
                            {dialog.type !== 'alert' && (
                                <button 
                                    onClick={() => handleClose(dialog.type === 'prompt' ? null : false)}
                                    style={{
                                        padding: '0.5rem 1rem',
                                        background: 'transparent',
                                        color: isLight ? '#4b5563' : '#a1a1aa',
                                        border: 'none',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontWeight: 500,
                                        fontSize: '0.9rem'
                                    }}
                                >
                                    {dialog.options.cancelText || 'Cancel'}
                                </button>
                            )}
                            <button 
                                autoFocus={dialog.type !== 'prompt'}
                                onClick={() => handleClose(dialog.type === 'prompt' ? dialog.inputValue : true)}
                                style={{
                                    padding: '0.5rem 1rem',
                                    background: '#3b82f6',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontWeight: 500,
                                    fontSize: '0.9rem'
                                }}
                            >
                                {dialog.options.confirmText || 'OK'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </DialogContext.Provider>
    );
};
